class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.handleClick);
  }

  connectedCallback() {
    this.load();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  load() {
    const productId = this.dataset.productId;
    if (!productId || !this.dataset.url || !this.dataset.sectionId) return;

    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!text) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const upsell = html.querySelector('.cart-drawer-upsell');
        if (!upsell) return;
        this.replaceChildren(upsell);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }

  handleClick(event) {
    const button = event.target.closest('.cart-drawer-upsell__add');
    if (!button || button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

    const cart = this.closest('cart-drawer');
    const variantId = button.dataset.variantId;
    if (!cart || !variantId || typeof routes === 'undefined') return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.showError('');

    const config = fetchConfig('json');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: cart
        .getSectionsToRender()
        .map((section) => section.id)
        .join(','),
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.showError(response.description || response.message);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-upsell',
            productVariantId: variantId,
            cartData: response,
          });
        }

        cart.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
        this.showError(window.cartStrings?.error);
      })
      .finally(() => {
        if (!button.isConnected) return;
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
  }

  showError(message) {
    const error = this.querySelector('.cart-drawer-upsell__error');
    if (!error) return;
    error.hidden = !message;
    error.textContent = message || '';
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
